import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Construction } from 'lucide-react';
import { RegisterPage } from '@/pages/RegisterPage';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { UserManagementPage } from '@/pages/admin/UserManagementPage';
import { SystemConfigPage } from '@/pages/admin/SystemConfigPage';
import { ClaimsQueuePage } from '@/pages/admin/ClaimsQueuePage';
import { AdminAllClaimsPage } from '@/pages/admin/AdminAllClaimsPage';
import { FinanceQueuePage } from '@/pages/finance/FinanceQueuePage';
import { ExportClaimsPage } from '@/pages/finance/ExportClaimsPage';
import { HrQueuePage } from '@/pages/hr/HrQueuePage';
import { ClaimsPage } from '@/pages/ClaimsPage';
import { ClaimDetailPage } from '@/pages/ClaimDetailPage';
import { SubmissionConfirmationPage } from '@/pages/claims/SubmissionConfirmationPage';
import { ProtectedRoute, SessionWatcher } from '@/components/ProtectedRoute';
import { AppLayout } from '@/components/AppLayout';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotificationsPage } from '@/pages/NotificationsPage';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

export function AppRouter() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionWatcher>
          <Routes>
          {/* Auth routes (no layout) */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          {/* Protected app routes (with layout) */}
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/claims" element={<ProtectedRoute><AppLayout><ClaimsPage /></AppLayout></ProtectedRoute>} />
          {/* Story 4.4: Confirmation page — must be before /:claimId to avoid match conflict */}
          <Route path="/claims/:claimId/confirmation" element={<ProtectedRoute><AppLayout><SubmissionConfirmationPage /></AppLayout></ProtectedRoute>} />
          <Route path="/claims/:claimId" element={<ProtectedRoute><AppLayout><ClaimDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>} />

          {/* Admin routes */}
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['ADMIN', 'IT_ADMIN']}><AppLayout><UserManagementPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/config" element={<ProtectedRoute allowedRoles={['IT_ADMIN']}><AppLayout><SystemConfigPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/all-claims" element={<ProtectedRoute allowedRoles={['ADMIN', 'IT_ADMIN', 'HR', 'FINANCE', 'FINANCE_LEAD']}><AppLayout><AdminAllClaimsPage /></AppLayout></ProtectedRoute>} />
          {/* Story 5.1–5.3: Admin Claims Queue */}
          <Route path="/admin/claims-queue" element={<ProtectedRoute allowedRoles={['ADMIN', 'IT_ADMIN']}><AppLayout><ClaimsQueuePage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['ADMIN', 'IT_ADMIN']}><AppLayout><PlaceholderPage title="Admin" /></AppLayout></ProtectedRoute>} />
          {/* Story 5.5–5.7: Finance Queue */}
          <Route path="/finance/claims-queue" element={<ProtectedRoute allowedRoles={['FINANCE', 'FINANCE_LEAD']}><AppLayout><FinanceQueuePage /></AppLayout></ProtectedRoute>} />
          <Route path="/finance/export" element={<ProtectedRoute allowedRoles={['STAFF', 'FINANCE', 'FINANCE_LEAD', 'ADMIN']}><AppLayout><ExportClaimsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/finance/*" element={<ProtectedRoute allowedRoles={['FINANCE', 'FINANCE_LEAD']}><AppLayout><PlaceholderPage title="Finance" /></AppLayout></ProtectedRoute>} />

          {/* Issue #15 (B2): HR Queue & Claims History */}
          <Route path="/hr/claims-queue" element={<ProtectedRoute allowedRoles={['HR', 'IT_ADMIN']}><AppLayout><HrQueuePage /></AppLayout></ProtectedRoute>} />
          <Route path="/hr/all-claims" element={<ProtectedRoute allowedRoles={['HR', 'IT_ADMIN']}><AppLayout><AdminAllClaimsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/hr/*" element={<ProtectedRoute allowedRoles={['HR', 'IT_ADMIN']}><AppLayout><PlaceholderPage title="HR" /></AppLayout></ProtectedRoute>} />

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<PlaceholderPage title="404 — Not Found" />} />
          </Routes>
        </SessionWatcher>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Will be replaced as stories implement each page
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center text-text">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-2 text-text-3">
        <Construction className="h-8 w-8" strokeWidth={2} />
      </div>
      <h1 className="text-h2">Robust Claim System</h1>
      <p className="text-text-2">{title} — Coming soon</p>
    </div>
  );
}

